import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getBrandSettings,
  getCategories,
  getAreas,
  getTexts,
  selectUser,
  getRentalData,
  getServices,
  getBookings,
  getCarEquipments,
  getFeatures,
  searchCars,
  searchCarsCompact,
  selectRegionChangeNo,
  setRegionChangeNo,
  REGION_CHANGE_THRESHOLD,
  isKYCApp,
  selectIsVerifyingGDPREmail,
  selectIsEditingPhone,
  getUser,
  verifyJumioSuccess,
  setIsEmbedded,
  setFrom,
  setBackBy,
  selectBrandSettings,
  loadIntercomScript,
  isUBQ,
  getBrandName,
  selectConfig,
} from 'getaway-data-layer';
import { setCustomBasicAuth } from 'getaway-data-layer/src/redux/store/userData/user/actions';
import LoadingOverlay from 'react-loading-overlay';

type props = {
  children: React.ReactElement;
};

const useQuery = () => {
  return new URLSearchParams(window.location.search);
};

const Data = ({ children }: props) => {
  const dispatch = useDispatch();
  const query = useQuery();
  const isEmbedded = query?.get('mode') === 'embedded';
  const magicLink = query?.get('l');
  const base6Magic = magicLink ? btoa(magicLink) : null;
  const displayIntercom = query?.get('support') === 'open';
  const user: any = useSelector(selectUser);
  // Ensure API baseURL is initialized before dispatching API actions on mount
  const { apiBaseURL } = useSelector(selectConfig);
  const regionChangeNo: number = useSelector(selectRegionChangeNo);
  const isVerifyingGDPREmail = useSelector(selectIsVerifyingGDPREmail);
  const isEditingPhone = useSelector(selectIsEditingPhone);
  const brandSettings = useSelector(selectBrandSettings);

  const [isAuthenticating, setIsAuthenticating] = useState(!!magicLink);

  const bubBrandId =
    process.env.VITE_STAGE === 'production'
      ? '68d2b8cdfc85d1466c5a0a29'
      : '68db99e99c525855188c70a1';

  useEffect(() => {
    dispatch(setIsEmbedded(isEmbedded));
  }, []);

  useEffect(() => {
    // @ts-ignore
    if (displayIntercom && brandSettings?.intercomAppId) {
      setTimeout(() => {
        const customBrand = getBrandName(
          process.env.VITE_X_BRAND_ID
        )?.toUpperCase();
        // @ts-ignore
        if (window?.Intercom) {
          // @ts-ignore
          window?.Intercom('update', {
            ...(user && {
              email: user.email,
              name: `${user.firstName} ${user.lastName}`,
              user_id: user.id,
              custom_user_status: user?.blocked ? 'blocked' : 'active',
              custom_block_reason: user?.blockReasonDetail,
              custom_tariff: user?.tariff?.name,
              custom_operatorID: user.merchantSystemData?.[0]?.merchantReference
                ? `${user.merchantSystemData?.[0]?.merchantReference}_${user.merchantSystemData?.[0]?.merchantSystem}`
                : '',
            }),
            custom_brand: customBrand,
          });
          // @ts-ignore
          window?.Intercom('show');
        } else {
          // force load then open
          loadIntercomScript(brandSettings?.intercomAppId, true, user);
        }
      }, 2000);
    }
  }, [brandSettings]);

  useEffect(() => {
    const fromParam = query.get('from');
    const backByParam = query.get('backBy');

    if (fromParam) {
      dispatch(setFrom(new Date(fromParam).toUTCString()));
    }
    if (backByParam) {
      dispatch(setBackBy(new Date(backByParam).toUTCString()));
    }
  }, []);

  useEffect(() => {
    if (magicLink) {
      dispatch(
        setCustomBasicAuth(`Bearer ${base6Magic}`, () => {
          dispatch(
            getUser(
              () => setIsAuthenticating(false),
              () => setIsAuthenticating(false)
            )
          );
        })
      );
    } else {
      setIsAuthenticating(false);
    }
  }, [base6Magic, dispatch]);

  useEffect(() => {
    if (!isKYCApp()) {
      setTimeout(() => {
        dispatch(
          searchCars(null, false, true, () => {
            dispatch(searchCars(1500000));
          })
        );
      }, 200);
      if (isUBQ()) {
        setTimeout(() => {
          dispatch(
            searchCars(
              null,
              false,
              true,
              () => {
                dispatch(
                  searchCars(1500000, false, false, () => {}, bubBrandId)
                );
              },
              bubBrandId
            )
          );
        }, 200);
      }
    }

    // @ts-ignore
    if (!user && window?.Intercom) {
      // @ts-ignore
      window?.Intercom('shutdown');
      // @ts-ignore
      window?.Intercom('boot', {
        custom_brand: getBrandName(process.env.VITE_X_BRAND_ID)?.toUpperCase(),
      });
    }
    if (!isVerifyingGDPREmail && !isEditingPhone) {
      if (user) {
        // @ts-ignore
        if (window?.Intercom) {
          // @ts-ignore
          window?.Intercom('boot', {
            email: user.email,
            name: `${user.firstName} ${user.lastName}`,
            user_id: user.id,
            custom_user_status: user?.blocked ? 'blocked' : 'active',
            custom_block_reason: user?.blockReasonDetail,
            custom_tariff: user?.tariff?.name,
            custom_brand: getBrandName(
              process.env.VITE_X_BRAND_ID
            )?.toUpperCase(),
            custom_operatorID: user.merchantSystemData?.[0]?.merchantReference
              ? `${user.merchantSystemData?.[0]?.merchantReference}_${user.merchantSystemData?.[0]?.merchantSystem}`
              : '',
          });
        }
        if (!isKYCApp()) {
          setTimeout(() => {
            new Promise<void>((resolve) => {
              dispatch(getRentalData());
              dispatch(
                getServices(() => {
                  resolve();
                })
              );
            })
              .then(() => {
                dispatch(getBookings(null, false));
              })
              .then(() => {
                dispatch(getCarEquipments());
              })
              .then(() => {
                dispatch(getFeatures());
              });
          }, 300);
        }
      }
      setTimeout(() => {
        new Promise<void>((resolve) => {
          dispatch(getBrandSettings());
          dispatch(getTexts(() => resolve()));
        })
          .then(() => {
            if (!isKYCApp()) {
              dispatch(getAreas(isUBQ()));
            }
          })
          .then(() => {
            if (!isKYCApp()) {
              dispatch(getCategories());
            }
          });
      }, 500);
    }
  }, [user?.version]);

  // verifyJumioSuccess when coming back from jumio scanning stage
  useEffect(() => {
    // Avoid race where apiBaseURL isn't set yet
    if (user?.email && !isAuthenticating && apiBaseURL) {
      const urlParams = new URLSearchParams(window.location.search);
      const transactionReference = urlParams.get('transactionReference');
      const workflowExecutionId = urlParams.get('workflowExecutionId');
      if (workflowExecutionId) {
        dispatch(verifyJumioSuccess(workflowExecutionId));
      } else if (transactionReference) {
        dispatch(verifyJumioSuccess(transactionReference));
      }
    }
  }, [user?.email, dispatch, apiBaseURL, isAuthenticating]);

  useEffect(() => {
    if (
      !isEmbedded &&
      regionChangeNo > REGION_CHANGE_THRESHOLD &&
      !isKYCApp()
    ) {
      dispatch(setRegionChangeNo(0));
      dispatch(searchCarsCompact());
      if (isUBQ()) {
        dispatch(searchCarsCompact(bubBrandId));
      }
    }
  }, [regionChangeNo]);

  if (isAuthenticating) {
    return <LoadingOverlay active spinner className="loading-overlay" />;
  }

  return children;
};

export default Data;
